<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useStore } from "vuex";
import Loading from "@/components/Loading";
import { useBranding } from "@/utils/branding";

// const searchQuery = ref("");
const showDropdown = ref(false);
const isLoading = ref(false);

const router = useRouter();
const store = useStore();
const route = useRoute();

// Get branding information
const { brandLogo, brandName } = useBranding();

// Get role from Vuex store (secure - fetched from API)
const role = computed(() => store.getters['auth/userRole']);

// Fetch user profile on mount to ensure role is loaded
onMounted(async () => {
  if (!store.getters['auth/userRole']) {
    await store.dispatch('auth/fetchUserProfile');
  }
});

const isDetailCompanyPage = computed(() => route.name === "DetailCompany");
const isDocumentCompanyPage = computed(() => route.name === "DocumentCompany");
const isBankCompanyPage = computed(() => route.name === "BankCompany");
const isCompanyPage = computed(
  () => isDetailCompanyPage.value || isDocumentCompanyPage.value || isBankCompanyPage.value
);

const isSandbox = computed(() => store.getters.isSandboxMode);

// Set to false to show the product tabs to all merchants
const PRODUCT_TABS_DEMO_ONLY = true;
const showProductTabs = computed(
  () =>
    !isCompanyPage.value &&
    (!PRODUCT_TABS_DEMO_ONLY || localStorage.getItem("merchantName") === "AKUN DEMO")
);

const getProductAttr = () => {
  try {
    return JSON.parse(localStorage.getItem("merchantProductAttr")) || [];
  } catch (error) {
    return [];
  }
};

const productTabs = computed(() => {
  const tabs = [{ key: "domestic", label: "Domestic" }];
  if (getProductAttr().includes("Q121s")) {
    tabs.push({ key: "payment-acceptance", label: "Payment Acceptance" });
  }
  return tabs;
});
const activeTab = ref("domestic");

const toggleDropdown = () => {
  showDropdown.value = !showDropdown.value;
};

const handleLogout = async () => {
  isLoading.value = true;
  try {
    const result = await store.dispatch("auth/logout");
    if (result.success) {
      router.push({ name: "Signin" });
    }
  } catch (error) {
    console.error('Error during logout:', error);
    // Fallback to manual cleanup if logout action fails
    localStorage.removeItem("auth_token");
    localStorage.removeItem("merchantEmail");
    localStorage.removeItem("merchant_user_email");
    localStorage.removeItem("merchantId");
    localStorage.removeItem("merchantKyc");
    localStorage.removeItem("merchantName");
    localStorage.removeItem("merchant_email");
    localStorage.removeItem("merchantAppId");
    localStorage.removeItem("merchantAppSecret");
    localStorage.removeItem("merchantStatusKyb");
    localStorage.removeItem("merchantProductAttr");
    router.push({ name: "Signin" });
  } finally {
    isLoading.value = false;
  }
};

const handleClose = () => {
  router.push({ name: "Home" });
};
</script>

<template>
  <header class="app-header" :class="{ 'is-sandbox': isSandbox }">
    <div v-if="isSandbox" class="sandbox-banner">
      This is a sandbox environment—transactions here are for testing only and
      have no real-world impact.
    </div>
  <nav class="navbar">
    <div class="container-fluid">
      <div class="navbar-left">
        <router-link class="logo-container" to="/">
          <img :src="brandLogo" :alt="brandName" />
          <span v-if="isCompanyPage" class="page-title">Verified Account</span>
        </router-link>
      </div>

      <div v-if="showProductTabs" class="navbar-center">
        <div class="product-tabs" role="tablist">
          <button
            v-for="tab in productTabs"
            :key="tab.key"
            type="button"
            role="tab"
            class="product-tab"
            :class="{ active: activeTab === tab.key }"
            :aria-selected="activeTab === tab.key"
            @click="activeTab = tab.key"
          >
            {{ tab.label }}
          </button>
        </div>
      </div>

      <div class="navbar-right">
        <div v-if="isCompanyPage">
          <span class="text-danger" style="cursor: pointer" @click="handleClose"
            >Close</span
          >
        </div>
        <template v-else>
          <div class="d-flex gap-4" style="align-items: center">
            <!-- <div class="search-container">
              <input
                type="text"
                class="search-input"
                placeholder="Search Transaction ID"
                v-model="searchQuery"
              />
              <i class="fas fa-search search-icon"></i>
            </div> -->

            <div class="notification-icon">
              <img
                src="/assets/icons/ic_notification.svg"
                alt="notification icon"
                width="20"
                height="20"
              />
            </div>

            <div class="user-profile">
              <div class="avatar" @click.stop="toggleDropdown">
                <i class="fas fa-user"></i>
              </div>
              <div v-if="showDropdown" class="dropdown-menu">
                <ul>
                  <li v-if="role === 'Admin'">
                    <router-link to="/profile" @click="showDropdown = false"
                      >Profile</router-link
                    >
                  </li>
                  <!-- <li><router-link to="/" @click="showDropdown = false">Settings</router-link></li> -->
                  <li @click="handleLogout">Logout</li>
                </ul>
              </div>
              <i
                :class="
                  showDropdown ? 'fas fa-chevron-up' : 'fas fa-chevron-down'
                "
                class="arrow-icon"
              ></i>
            </div>
          </div>
        </template>
      </div>
    </div>
    <Loading :isLoading="isLoading" />
  </nav>
  </header>
</template>

<style scoped>
.app-header.is-sandbox {
  background-color: #e0712c;
}

.sandbox-banner {
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
  color: #ffffff;
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.navbar {
  position: relative;
  overflow: visible;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  padding: 12px 24px;
  background-color: #ffffff;
  border-bottom: 1px solid #e9ecef;
}

.is-sandbox .navbar {
  border-radius: 16px 16px 0 0;
}

.navbar-center {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
}

.product-tabs {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px;
  background-color: #e3e6e6;
  border-radius: 100px;
}

.product-tab {
  padding: 4px 16px;
  border: none;
  border-radius: 100px;
  background: transparent;
  color: #4b5563;
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.product-tab:hover:not(.active) {
  color: #1f2937;
}

.product-tab.active {
  background-color: #43bb75;
  color: #ffffff;
}

.container-fluid {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.navbar-left {
  display: flex;
  align-items: center;
  gap: 24px;
}

.page-title {
  font-size: 18px;
  font-weight: 600;
  color: #212529;
}

.navbar-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.search-container {
  position: relative;
}

.search-input {
  padding: 8px 12px 8px 36px;
  border: 1px solid #ced4da;
  border-radius: 20px;
  font-size: 14px;
  width: 240px;
  height: 32px;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
}

.notification-icon {
  display: flex;
  width: 36px;
  height: 36px;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  border-radius: 100px;
  background: var(--Background-Light-Grey, #f4f6f6);
  cursor: pointer;
}

.user-profile {
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
}

.avatar {
  width: 36px;
  height: 36px;
  background-color: #dde0e0;
  border: 2px solid #ffffff;
  box-shadow: 0 0 0 1px #e5e7eb;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-size: 14px;
}

.arrow-icon {
  font-size: 14px;
  color: #6b7280;
}

.logo-container {
  display: flex;
  align-items: center;
  text-decoration: none;
}

.logo-container img {
  height: 40px;
}

.dropdown-menu {
  position: absolute;
  top: 40px;
  right: 0;
  background-color: #ffffff;
  border: 1px solid #ced4da;
  border-radius: 4px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  width: 150px;
  z-index: 1050;
  display: block;
}

.dropdown-menu ul {
  list-style: none;
  margin: 0;
  padding: 0;
}

.dropdown-menu li {
  padding: 8px 12px;
  cursor: pointer;
  font-size: 14px;
  color: #212529;
}

.dropdown-menu li:hover {
  background-color: #f8f9fa;
}

.dropdown-menu li a {
  text-decoration: none;
  color: inherit;
  display: block;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@media (max-width: 768px) {
  .navbar-center {
    position: static;
    transform: none;
    order: 3;
    width: 100%;
  }

  .navbar-left {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    width: 100%;
  }

  .logo-container {
    order: 1;
  }

  .navbar-right {
    flex-wrap: wrap;
    gap: 8px;
  }

  .search-container {
    order: 3;
    width: 100%;
    margin-top: 8px;
  }

  .search-input {
    width: 100%;
  }

  .notification-icon {
    order: 1;
  }

  .user-profile {
    order: 2;
  }
}
</style>
